<template>
  <div v-if="isMobile" class="map-container-mobile"></div>
  <div v-else class="map-container"></div>
</template>

<script>
import { loadModules } from 'esri-loader';
import { mapGetters, mapMutations, mapActions } from 'vuex';
import mapConfig from '../config/mapConfig';
import appConfig from '../config/appConfig';
import MapConfigHelper from '../helpers/MapConfigHelper';
import MapViewHelper from '../helpers/MapViewHelper';
import GeometryHelper from '../helpers/GeometryHelper';

import Vue from 'vue';
import MapLegend from '../components/map-legend';
import MapHomeButton from '../components/map-home-button';

import ClientsideLocations from '../api/ClientsideLocations';
import ClientsideFacilities from '../api/ClientsideFacilities';

const hiInsetMapContainerId = 'hiInsetMap';

export default {
  name: 'web-map',
  data: () => ({
    globalExtent: undefined,
    usaExtent: undefined,
    hiInsetOuterDisplayExtent: undefined,
    hiInsetInnerDisplayExtent: undefined,
    insetMap: undefined
  }),
  computed: {
    ...mapGetters({
      isMobile: 'mapTemplate/isMobile',
      mapView: 'mapTemplate/mapView',
      mapTemplate: 'profileConfig/mapTemplate',
      mapProfileLoaded: 'profileConfig/mapProfileLoaded',
      countryExtents: 'mapTemplate/countryExtents',
      facilityFeatureLayer: 'mapTemplate/facilityFeatureLayer',
      pipelineFeatureLayer: 'mapTemplate/pipelineFeatureLayer',
      transmissionFeatureLayer: 'mapTemplate/transmissionFeatureLayer',
      locationFeatureLayer: 'mapTemplate/locationFeatureLayer',
      mapFilterEnergy: 'filterData/mapFilterEnergy',
      mapFilterStatus: 'filterData/mapFilterStatus',
      mapFilterEnergyClause: 'filterData/mapFilterEnergyClause',
      mapFilterStatusClause: 'filterData/mapFilterStatusClause'
    })
  },
  methods: {
    ...mapMutations({
      setMapView: 'mapTemplate/setMapView',
      setMapLoaded: 'mapTemplate/setMapLoaded',
      setLoading: 'mapTemplate/setLoading'
    }),
    ...mapActions({
      selectItem: 'mapTemplate/selectItem',
      setFilter: 'mapTemplate/setMapFilter',
      updateTableData: 'tableData/updateTableData'
    }),
    setExtentProperties: async function () {
      const [webMercatorUtils] = await loadModules([
        'esri/geometry/support/webMercatorUtils'
      ]);
      // TODO - Add some error checking around these extent setters!
      this.globalExtent = this.countryExtents.filter(
        (f) => f.name == appConfig.countriesApiProps.globalCountryValue
      )[0].extent;
      this.usaExtent = this.countryExtents.filter(
        (f) => f.name == appConfig.countriesApiProps.usaCountryValue
      )[0].extent;
      this.hiInsetOuterDisplayExtent = this.globalExtent.clone().expand(2.2);
      this.hiInsetInnerDisplayExtent = this.usaExtent.clone().expand(0.9);
    },
    createBasemapGallery: async function () {
      const [BasemapGallery, LocalBasemapsSource, Expand] = await loadModules([
        'esri/widgets/BasemapGallery',
        'esri/widgets/BasemapGallery/support/LocalBasemapsSource',
        'esri/widgets/Expand'
      ]);

      const basemaps = await MapConfigHelper.getAllBasemaps();
      const basemapGallery = new BasemapGallery({
        source: new LocalBasemapsSource({ basemaps }),
        container: document.createElement('div'),
        view: this.mapView
      });

      const basemapGalleryExpandNode = new Expand({
        content: basemapGallery,
        expandIconClass: 'esri-icon-basemap',
        expandTooltip: 'Basemap Gallery',
        container: document.createElement('div'),
        view: this.mapView
      });
      return basemapGalleryExpandNode;
    },
    createHomeButton: function () {
      const MapHomeButtonComponentClass = Vue.extend(MapHomeButton);
      const home = new MapHomeButtonComponentClass();
      home.$mount();
      return home.$el;
    },
    createLegend: async function () {
      const [
        esriRequest,
        UniqueValueRenderer,
        SimpleRenderer,
        Expand
      ] = await loadModules([
        'esri/request',
        'esri/renderers/UniqueValueRenderer',
        'esri/renderers/SimpleRenderer',
        'esri/widgets/Expand'
      ]);

      const legendDiv = document.createElement('div');
      legendDiv.id = 'legendPanel';
      legendDiv.classList.add('legend-panel');

      const generateLegend = (renderer) => {
        try {
          const MapLegendComponentClass = Vue.extend(MapLegend);
          const updatedRenderers = renderer.uniqueValueInfos.filter(
            (i) =>
              !appConfig.excludeLegendItems.includes(i.label) ||
              i.symbol.type === 'simple-line'
          );
          const legend = new MapLegendComponentClass({
            propsData: { legendItems: updatedRenderers }
          });
          legend.$mount();
          legendDiv.append(legend.$el);
        } catch (error) {
          legendError = true;
          console.error(`Error generating legend: ${error}`);
        }
      };

      const url = appConfig.assetTypeApiProps.url;
      const pipelineUrl = appConfig.assetTypeApiProps.pipeline;
      const transmissionUrl = appConfig.assetTypeApiProps.transmission;
      let legendError = false;
      Promise.all([
        esriRequest(url, {}),
        esriRequest(pipelineUrl, {}),
        esriRequest(transmissionUrl, {})
      ])
        .then((results) => {
          const allRenderers = {
            uniqueValueInfos: []
          };
          results.map((response) => {
            var json = response.data;
            if (json.drawingInfo.renderer.type === 'simple') {
              const renderer = SimpleRenderer.fromJSON(
                json.drawingInfo.renderer
              );
              allRenderers.uniqueValueInfos.push(renderer);
            } else {
              const renderer = UniqueValueRenderer.fromJSON(
                json.drawingInfo.renderer
              );
              renderer.uniqueValueInfos.map((info) => {
                allRenderers.uniqueValueInfos.push(info);
              });
            }
          });
          generateLegend(allRenderers);
        })
        .catch((err) => {
          legendError = true;
          console.error(`Error making Legend request: ${err}`);
        })
        .finally(function () {
          if (legendError) {
            legendDiv.innerHTML = 'Legend unavailable';
          }
        });

      // Note: the Expand is returned before the esri request completes, but the legend content
      // gets populated nonetheless.
      return new Expand({
        content: legendDiv,
        expandIconClass: 'esri-icon-layer-list',
        expandTooltip: 'Legend',
        container: document.createElement('div'),
        view: this.mapView
      });
    },
    setExtentWatcher: async function () {
      const [watchUtils] = await loadModules(['esri/core/watchUtils']);
      watchUtils.whenTrue(this.mapView, 'stationary', () => {
        if (this.mapView.container) {
          if (this.showHawaiiInsetMap()) {
            this.addHiInsetMap();
          } else {
            this.removeHiInsetMap();
          }
        }
      });
    },
    showHawaiiInsetMap: function () {
      return (
        this.hiInsetOuterDisplayExtent.contains(this.mapView.extent) &&
        this.mapView.extent.contains(this.hiInsetInnerDisplayExtent)
      );
    },
    addHiInsetMap: async function () {
      if (this.mapView.ui.find(hiInsetMapContainerId)) {
        return;
      }
      const [
        ArcGISMap,
        MapView,
        FeatureLayer,
        Extent,
        Query
      ] = await loadModules(
        [
          'esri/Map',
          'esri/views/MapView',
          'esri/layers/FeatureLayer',
          'esri/geometry/Extent',
          'esri/tasks/support/Query'
        ],
        { css: true }
      );
      const hiMapLayers = [];

      // Note: Runtime errors thrown if clientside FeatureLayer instance from
      // Vuex store added to more than one map. Clone here, including limiting
      // facilities to just those for Hawaii (to reduce size of cloned layer).
      const query = new Query();
      query.where = `LOWER(${appConfig.facilityApiProps.stateField}) = LOWER('${appConfig.facilityApiProps.hawaiiStateValue}')`;
      query.returnGeometry = true;
      query.outFields = ['*'];
      const response = await this.facilityFeatureLayer.queryFeatures(query);
      const hiClientsideFeatureLayer = new FeatureLayer({
        source: response.features,
        objectIdField: this.facilityFeatureLayer.objectIdField,
        geometryType: this.facilityFeatureLayer.geometryType,
        visible: this.facilityFeatureLayer.visible,
        renderer: this.facilityFeatureLayer.renderer,
        fields: this.facilityFeatureLayer.fields
      });
      hiMapLayers.push(hiClientsideFeatureLayer);

      let hiBasemap;
      if (this.mapView && this.mapView.map) {
        hiBasemap = this.mapView.map.basemap;
      } else {
        // should never happen
        hiBasemap = await MapConfigHelper.getDefaultBasemap();
      }

      const hiMap = new ArcGISMap({
        basemap: hiBasemap,
        layers: hiMapLayers
      });
      this.insetMap = hiMap;
      const hiMapDiv = document.createElement('div');
      hiMapDiv.id = hiInsetMapContainerId;
      hiMapDiv.classList.add('inset-map-container');
      const hiView = new MapView({
        container: hiMapDiv,
        map: hiMap,
        extent: new Extent(mapConfig.hawaiiInsetMapExtent),
        constraints: {
          snapToZoom: false
        }
      });
      hiView.ui.components = [];

      this.mapView.ui.add(hiMapDiv, 'bottom-left');
      hiView.when(MapViewHelper.disableZooming).then(() => {
        hiView.on(['click', 'double-click'], () => {
          this.selectItem({
            value: appConfig.locationApiProps.hawaiiStateValue,
            type: 'Geography'
          });
        });
      });
    },
    removeHiInsetMap: function () {
      this.mapView.ui.remove(hiInsetMapContainerId);
    },
    setMapUIControls: async function () {
      const basemapGalleryExpandNode = await this.createBasemapGallery();
      const legendExpandNode = await this.createLegend();
      const homeButton = this.createHomeButton();
      this.mapView.ui.add(basemapGalleryExpandNode, 'bottom-right');
      this.mapView.ui.add(homeButton, 'bottom-right');
      this.mapView.ui.move('zoom', 'bottom-right');
      this.mapView.ui.add(legendExpandNode, 'bottom-right');
      // Need to show/hide HI inset map based on extent
      this.setExtentWatcher();
      // Enable selecting of state/province or facility on map click
      this.mapView.on('click', async (event) => {
        this.getFeatureAtMapClick(event.mapPoint);
      });
      this.limitPanning();
    },
    limitPanning: async function () {
      const [Extent] = await loadModules(['esri/geometry/Extent']);
      var initialExtent = new Extent(mapConfig.baseExtent);
      var hawaiiExtent = new Extent(mapConfig.hawaiiInsetMapExtent);
      //console.warn(initialExtent);
      const checkExtent = ((event) => {
        if (!event) {
          return;
        }
        //If the map has moved to the point where it's center is
        //outside the initial boundaries, then move it back to the
        //edge where it moved out
        var currentCenter = this.mapView.extent.center;
        if (
          !initialExtent.contains(currentCenter) &&
          !hawaiiExtent.contains(currentCenter)
        ) {
          var newCenter = this.mapView.extent.center;
          //console.warn(newCenter);

          //check each side of the initial extent and if the
          //current center is outside that extent,
          //set the new center to be on the edge that it went out on
          if (currentCenter.x <= initialExtent.xmin) {
            newCenter.x = initialExtent.xmin;
          }
          if (currentCenter.x >= initialExtent.xmax) {
            newCenter.x = initialExtent.xmax;
          }
          if (currentCenter.y <= initialExtent.ymin) {
            newCenter.y = initialExtent.ymin;
          }
          if (currentCenter.y >= initialExtent.ymax) {
            newCenter.y = initialExtent.ymax;
          }
          this.mapView.goTo(newCenter);
        }
      }).bind(this);
      this.mapView.watch('stationary', checkExtent);
    },
    finalizeMapControls: async function () {
      const [Map, MapView, watchUtils] = await loadModules(
        ['esri/Map', 'esri/views/MapView', 'esri/core/watchUtils'],
        { css: true }
      );

      await this.setExtentProperties();

      const mapLayers = [];
      mapLayers.push(this.pipelineFeatureLayer);
      mapLayers.push(this.transmissionFeatureLayer);
      mapLayers.push(this.facilityFeatureLayer);

      const defaultBasemap = await MapConfigHelper.getDefaultBasemap();

      const map = new Map({
        basemap: defaultBasemap,
        layers: mapLayers
      });

      // Inset map basemap should always match the main map basemap.
      map.watch('basemap', (newValue, oldValue, propertyName, target) => {
        if (this.insetMap) {
          this.insetMap.basemap = newValue;
        }
      });

      const mapView = new MapView({
        container: this.$el,
        map: map,
        extent: this.globalExtent,
        constraints: {
          snapToZoom: false,
          rotationEnabled: false,
          minScale: 45000000
        }
      });
      this.setMapView(mapView);

      this.mapView.when(() => {
        this.selectItem({
          value: appConfig.countriesApiProps.globalCountryValue,
          type: 'Country'
        });
        this.setMapUIControls();
        // watch for Project/Facilities feature layer loaded
        let assetsLayerPromise = new Promise((resolve, reject) => {
          this.mapView
            .whenLayerView(this.facilityFeatureLayer)
            .then((layerView) => {
              const handle = layerView.watch('updating', (updating) => {
                if (!updating) {
                  resolve(true);
                  // only fire once
                  if (handle) {
                    handle.remove();
                  }
                }
              });
            });
        });

        // watch for basemap loaded
        let basemapPromise = watchUtils.whenTrueOnce(
          defaultBasemap,
          'loaded',
          () => {}
        );

        Promise.all([assetsLayerPromise, basemapPromise]).then((values) => {
          this.setLoading(false);
          this.setMapLoaded(true);
        });
      });
    },
    getFeatureAtMapClick: async function (mapPoint) {
      const [geometryEngine] = await loadModules([
        'esri/geometry/geometryEngine'
      ]);

      // Buffer point using a tolerance based on fraction of extent width. This
      // makes the click/select experience consistent for user across different
      // map scales.
      const clickTolerance =
        this.mapView.extent.width * mapConfig.mapClickTolerance;
      const mapPointBuffer = geometryEngine.geodesicBuffer(
        mapPoint,
        clickTolerance,
        'meters'
      );
      var query = {
        geometry: mapPointBuffer,
        distance: '10',
        spatialRelationship: 'intersects',
        outFields: ['*'],
        returnGeometry: false
      };
      let record;
      const pipelines = await this.pipelineFeatureLayer.queryFeatures(query);
      if (pipelines && pipelines.features.length > 0) {
        record =
          pipelines.features[0].attributes[
            appConfig.facilityApiProps.nameField
          ];
      }
      if (!record) {
        const transmission = await this.transmissionFeatureLayer.queryFeatures(
          query
        );
        if (transmission && transmission.features.length > 0) {
          record =
            transmission.features[0].attributes[
              appConfig.facilityApiProps.nameField
            ];
        }
      }
      const facilities = new ClientsideFacilities(appConfig.facilityApiProps);
      const facName =
        record ||
        (await facilities.getFacilityNameByGeometry(
          this.facilityFeatureLayer,
          mapPointBuffer,
          this.mapFilterStatusClause + ' AND ' + this.mapFilterEnergyClause
        ));
      // Facility takes precedence - if one hit by (buffered) map click then
      // select it, otherwise try to select a state/province.
      if (facName) {
        this.selectItem({
          value: facName,
          type: 'Facility'
        });
      } else if (this.basemapHasStateOutlines()) {
        const locations = new ClientsideLocations(appConfig.locationApiProps);
        const locName = await locations.getLocationNameByGeometry(
          this.locationFeatureLayer,
          mapPoint
        );
        if (locName) {
          this.selectItem({
            value: locName,
            type: 'Geography'
          });
        } else {
          console.log(`Map click but no feature found.`);
        }
      } else {
        console.log(`Map click but no feature found.`);
      }
    },
    basemapHasStateOutlines: function () {
      return !mapConfig.basemapsWithoutStateOutlines.includes(
        this.mapView.map.basemap.id
      );
    }
  },
  watch: {
    mapProfileLoaded: function (newValue, oldValue) {
      if (newValue) {
        const finalize = async () => {
          await this.finalizeMapControls();
          //TODO: Need to be put in a better place not tied to map!
          //await this.updateTableData();
        };
        finalize();
      }
    },
    mapFilterEnergy: function (newValue, oldValue) {
      if (newValue) {
        this.setFilter();
      }
    },
    mapFilterStatus: function (newValue, oldValue) {
      if (newValue) {
        this.setFilter();
      }
    }
  },
  mounted() {
    this.setLoading(true);
  },
  beforeDestroy() {
    // Remove MapView, and watch handle from state before closing
    if (this.mapView) {
      this.setMapView(null);
    }
  },
  errorCaptured(err, vm, info) {
    alert('ERR CAPTURED in WEB-MAP.VUE: ', err);
  }
};
</script>

<style lang="scss">
.esri-ui-bottom-right {
  flex-flow: column !important;
}
.esri-ui-bottom-right .esri-component {
  margin-top: 10px;
}
.map-container {
  padding: 0;
  margin: 0;
  width: 100%;
  height: 100%;
}
.map-container-mobile {
  padding: 0;
  margin: 0;
  width: 100%;
  height: 95%;
}

.inset-map-container {
  background-color: lightgray;
  width: 200px;
  height: 138px;
  box-shadow: 0 3px 4px rgba(0, 0, 0, 0.3) !important;
}

div[title='Legend' i] {
  width: 62px;
  height: 32px;
  .esri-collapse__icon {
    display: none;
  }
  .esri-icon-font-fallback-text {
    width: auto;
    height: 13px;
    clip: auto;
    font-size: 12px;
    font-family: arial !important;
  }
}

.esri-view-width-greater-than-xsmall {
  .legend-panel {
    background-color: white;
    color: black;
    width: 560px;
    height: 120px;
    white-space: nowrap;
    padding: 3px;
  }
  .legend-container {
    white-space: nowrap;
    columns: 3;
    -webkit-column-count: 3;
    -moz-column-count: 3;
    column-count: 3;
    column-gap: 40px;
  }
}
</style>
